import { ImageIcon, Loader2, RotateCcw } from "lucide-react"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"
import type { TransitionFrameSide } from "@/lib/workspace-api"

/**
 * Половина коллажа перехода: сам кадр. Живёт внутри бокса формата кадра, поэтому
 * узкая — управление к ней не крепим, см. TransitionFrameControls.
 */
export function TransitionFrameHalf({
  url,
  side,
  manual,
  noFrameLabel,
}: {
  url: string | null
  side: TransitionFrameSide
  /** Кадр поставлен вручную: помечаем, иначе непонятно, почему он не меняется вслед за соседом. */
  manual: boolean
  /** Подпись пустой половины; пусто — когда её место занимает кнопка выбора. */
  noFrameLabel: string
}) {
  const { t } = useTranslation(["shot", "common"])
  const label = side === "left" ? t("preview.leftShotLastFrame") : t("preview.rightShotFirstFrame")

  return (
    <div className="relative w-1/2 overflow-hidden">
      {url ? (
        // w-[200%] + object-left/right: каждая половина показывает свою половину кадра.
        <img
          src={url}
          alt={label}
          className={cn("h-full w-[200%] max-w-none object-cover", side === "left" ? "object-left" : "object-right")}
        />
      ) : (
        <div className="flex h-full items-center justify-center text-xs text-ui-text-secondary dark:bg-ui-bg-elevated/80 light:bg-clay-2">
          {noFrameLabel}
        </div>
      )}

      {manual ? (
        <span
          className={cn(
            "absolute top-2 rounded bg-[var(--ui-chip-bg-strong)] px-1.5 py-0.5 text-[10px] font-medium text-ui-text-primary backdrop-blur-sm",
            side === "left" ? "left-2" : "right-2",
          )}
        >
          {t("transitionFrames.manual")}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Управление половиной перехода. Занимает половину всей сцены, а не узкую полоску кадра:
 * разделитель делит пополам именно сцену, поэтому и зоной выбора читается её половина —
 * внутри полоски кнопки выглядели прижатыми к разделителю.
 */
export function TransitionFrameControls({
  side,
  hasFrame,
  manual,
  busy,
  onPick,
  onReset,
}: {
  side: TransitionFrameSide
  hasFrame: boolean
  manual: boolean
  busy: boolean
  onPick: () => void
  onReset: () => void
}) {
  const { t } = useTranslation(["shot", "common"])

  return (
    <div
      className={cn(
        "group/half absolute inset-y-0 z-10 flex w-1/2 flex-col items-center justify-center gap-1.5 p-2 transition-opacity",
        side === "left" ? "left-0" : "right-0",
        // Пустую половину незачем открывать по наведению: закрывать нечего, а без
        // видимой кнопки экран выглядит тупиком — именно так и потерялась фича.
        // На тач-устройствах ховера нет вовсе: там управление держим видимым, иначе
        // кнопки «Выбрать кадр» и «Авто» невидимы, но кликабельны — тап по центру
        // половины открывал бы пикер без всякого повода.
        hasFrame
          ? cn(
              "opacity-0 focus-within:opacity-100 hover:bg-black/45 hover:opacity-100",
              "[@media(hover:none)]:bg-black/45 [@media(hover:none)]:opacity-100",
            )
          : "opacity-100",
      )}
    >
      <Button type="button" size="sm" variant="secondary" className="max-w-full" disabled={busy} onClick={onPick}>
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
        ) : (
          <ImageIcon className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="truncate">{t("transitionFrames.pick")}</span>
      </Button>
      {manual ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={onReset}
          title={t("transitionFrames.resetHint")}
          aria-label={t("transitionFrames.resetHint")}
        >
          <RotateCcw className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{t("transitionFrames.reset")}</span>
        </Button>
      ) : null}
    </div>
  )
}
